import type { AresLookupResponseDTO } from '@form-factory/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { useDebounce } from '@uidotdev/usehooks';
import { Label, toast } from '@utima/ui';
import { useFormApi } from 'informed';
import { useState, useEffect, useCallback, useRef } from 'react';

import { trpc } from '@/services/trpc';

interface AresLookupProps {
  onLookupSuccess?: (selectedCompany: AresLookupResponseDTO) => void;
  label?: string;
  placeholder?: string;
}

const MIN_NAME_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 500;

export function AresLookup({
  onLookupSuccess,
  label,
  placeholder,
}: AresLookupProps) {
  const { t } = useLingui();
  const formApi = useFormApi();
  const [inputValue, setInputValue] = useState('');
  const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_MS);
  const [suggestions, setSuggestions] = useState<AresLookupResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [lastExecutedQuery, setLastExecutedQuery] = useState<string | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const lookupMutation = trpc.billingInfo.lookupCzechCompany.useMutation();

  const isIco = (query: string) =>
    /^(\d *){8}$/.test(query.replaceAll(/\s/g, ''));
  const normalizeIco = (query: string) => query.replaceAll(/\s/g, '');

  const executeLookup = useCallback(
    async (query: string) => {
      if (query === lastExecutedQuery) {
        if (suggestions.length > 0) setShowDropdown(true);

        return;
      }

      setIsLoading(true);
      setShowDropdown(true);
      try {
        const data = await lookupMutation.mutateAsync({ query });
        const companyDetails = data && data.length > 0 ? data : null;
        setSuggestions(companyDetails || []);
        if (companyDetails === null && query.length >= MIN_NAME_QUERY_LENGTH) {
          // toast.info(t`No companies found for "${query}".`);
        }
        setLastExecutedQuery(query);
      } catch (error: any) {
        toast.error(t`ARES lookup failed: ${error.message}`);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [lookupMutation, t, lastExecutedQuery, suggestions.length],
  );

  useEffect(() => {
    if (debouncedInputValue.length === 0) {
      return;
    }

    const normalizedQuery = normalizeIco(debouncedInputValue);
    const isPotentiallyIco = isIco(normalizedQuery);

    if (isPotentiallyIco) {
      if (normalizedQuery !== lastExecutedQuery) {
        executeLookup(normalizedQuery);
      } else if (suggestions.length > 0) {
        setShowDropdown(true);
      }
    } else if (debouncedInputValue.trim().length >= MIN_NAME_QUERY_LENGTH) {
      if (debouncedInputValue.trim() !== lastExecutedQuery) {
        executeLookup(debouncedInputValue.trim());
      } else if (suggestions.length > 0) {
        setShowDropdown(true);
      }
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      if (lastExecutedQuery !== null) {
        setLastExecutedQuery(null);
      }
    }
  }, [debouncedInputValue, lastExecutedQuery, suggestions.length]);

  const prefillFormFields = (company: AresLookupResponseDTO) => {
    formApi.setValue('isCompany', true);

    setTimeout(() => {
      const currentFormValues = formApi.getFormState().values;
      const newValues = {
        ...currentFormValues,
        isCompany: true,
        companyName: company.companyName || '',
        registrationNumber:
          company.registrationNumber ||
          (isIco(inputValue) ? normalizeIco(inputValue) : ''),
        vatId: company.vatId || '',
        addressLine1: company.addressLine1 || '',
        addressLine2:
          company.addressLine2 || currentFormValues.addressLine2 || '',
        city: company.city || '',
        postalCode: company.postalCode || '',
        country: company.country || 'CZ',
      };
      console.log('Attempting to prefill (step 2) with:', newValues);
      formApi.setValues(newValues);

      setInputValue(company.companyName || '');
      setSuggestions([]);
      setShowDropdown(false);
      setFocusedIndex(-1);
      setLastExecutedQuery(company.companyName || '');
      if (onLookupSuccess) {
        onLookupSuccess(company);
      }
      toast.success(t`Company details prefilled: ${company.companyName}`);
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setInputValue(newQuery);
    if (newQuery.length === 0) {
      setSuggestions([]);
      setShowDropdown(false);
      setLastExecutedQuery(null);
    }
  };

  const handleSuggestionClick = (suggestion: AresLookupResponseDTO) => {
    prefillFormFields(suggestion);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!inputRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
      }
      setFocusedIndex(-1);
    }, 150);
  };

  const handleInputFocus = () => {
    if (
      inputValue.trim().length >= MIN_NAME_QUERY_LENGTH ||
      isIco(normalizeIco(inputValue))
    ) {
      if (
        suggestions.length > 0 ||
        (lastExecutedQuery === inputValue.trim() && suggestions.length > 0)
      ) {
        setShowDropdown(true);
      } else if (inputValue.trim() !== lastExecutedQuery) {
        // If input is valid and different from last query, trigger a lookup on focus if desired
        // For now, let's rely on debounced input or direct ICO lookup button for new queries.
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          e.preventDefault();
          prefillFormFields(suggestions[focusedIndex]);
        } else if (
          suggestions.length === 1 &&
          (inputValue.length >= MIN_NAME_QUERY_LENGTH ||
            isIco(normalizeIco(inputValue)))
        ) {
          e.preventDefault();
          prefillFormFields(suggestions[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  const [directIco, setDirectIco] = useState('');

  // Hidden for now, as primary input can do both
  const _handleDirectIcoLookup = () => {
    const normalized = normalizeIco(directIco);
    if (isIco(normalized)) {
      setInputValue(normalized);
      executeLookup(normalized);
      setDirectIco('');
    } else {
      toast.warning(t`Please enter a valid 8-digit IČO.`);
    }
  };

  return (
    <div className='mb-6 space-y-3 rounded-md border border-input bg-background/5'>
      <Label
        htmlFor='ares-universal-lookup'
        className='text-base font-semibold text-foreground'
      >
        {label || <Trans>ARES Company Lookup (CZ)</Trans>}
      </Label>

      <div className='flex items-start gap-x-3'>
        <div className='relative grow space-y-1'>
          <input
            id='ares-universal-lookup'
            type='text'
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t`Type Company Name or IČO...`}
            className='bg-input w-full rounded-md border border- px-3 py-2 text-sm ring-offset-background placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
            autoComplete='off'
            aria-expanded={showDropdown}
            aria-controls='ares-suggestions-list'
            ref={inputRef}
            aria-activedescendant={
              focusedIndex >= 0 ? `suggestion-${focusedIndex}` : undefined
            }
          />
          {showDropdown && (
            <ul
              id='ares-suggestions-list'
              className='absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'
              role='listbox'
            >
              {isLoading && (
                <li className='cursor-default select-none px-4 py-2 text-gray-700'>
                  <Trans>Loading suggestions...</Trans>
                </li>
              )}
              {!isLoading &&
                suggestions.length === 0 &&
                (inputValue.length >= MIN_NAME_QUERY_LENGTH ||
                  isIco(normalizeIco(inputValue))) &&
                lastExecutedQuery && (
                  <li className='cursor-default select-none px-4 py-2 text-gray-700'>
                    <Trans>No companies found.</Trans>
                  </li>
                )}
              {!isLoading &&
                suggestions.map((s, index) => (
                  <li
                    id={`suggestion-${index}`}
                    key={
                      s.registrationNumber ||
                      `suggestion-${index}-${s.companyName}`
                    }
                    onClick={() => handleSuggestionClick(s)}
                    onMouseDown={e => e.preventDefault()}
                    className={`relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 ${
                      focusedIndex === index
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-accent'
                    }`}
                    role='option'
                    aria-selected={focusedIndex === index}
                  >
                    {s.companyName} ({s.registrationNumber})
                    <span className='text-muted-foreground block text-xs'>
                      {s.addressLine1}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>

        {/* <div className='space-y-1 pt-1'>
          <div className='flex items-center gap-2'>
            <input
              id='ares-direct-ico'
              type='text'
              value={directIco}
              onChange={e => setDirectIco(e.target.value)}
              placeholder={t`8-digit IČO`}
              maxLength={8}
              className='w-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
            />
            <Button
              type='button'
              variant='secondary'
              onClick={handleDirectIcoLookup}
              loading={
                lookupMutation.isPending &&
                normalizeIco(directIco) === normalizeIco(debouncedInputValue) &&
                directIco.length > 0
              }
              size='sm'
              className='px-3'
            >
              <Trans>OK</Trans>
            </Button>
          </div>
          <Label
            htmlFor='ares-direct-ico'
            className='text-muted-foreground whitespace-nowrap text-xs'
          >
            <Trans>Direct IČO Lookup</Trans>
          </Label>
        </div> */}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className='sr-only' aria-live='polite'>
          {t`${suggestions.length} companies found. Use arrow keys and Enter to select.`}
        </div>
      )}
      <div className='ml-2 pt-1 text-xs text-gray-500'>
        <Trans>Enter Company Name (min. 3 chars) or 8-digit IČO</Trans>
      </div>
    </div>
  );
}
